<template>
    <div v-if="!isLoading" class="EntityDetailsCashJournalTransactions">
        <BaseDashboardTable
            :columns="columns"
            :items="formattedTransactions"
            :empty-message="'Keine Transaktionen für diesen Monat gefunden'"
            :pagination="{ enabled: false }"
            container-height="400px"
            :has-search="true"
            search-placeholder="Transaktionen suchen..."
        >
            <template #column-type="{ item }">
                <Badge :color-id="getBadgeColorId(item.type)" size="small">
                    {{ item.typeLabel }}
                </Badge>
            </template>

            <template #column-amount="{ item }">
                <span :class="item.amount >= 0 ? 'amount-positive' : 'amount-negative'">
                    {{ item.formattedAmount }}
                </span>
            </template>

            <template #column-timestamp="{ item }">
                {{ item.formattedDate }}
            </template>
        </BaseDashboardTable>
    </div>
</template>

<script>
import BaseDashboardTable from '@/components/BaseDashboardTable.vue';
import Badge from '@/components/widgets/Badge.vue';
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { de } from 'date-fns/locale';

const TRANSACTION_TYPE_LABELS = {
    income: 'Einnahme',
    deposit: 'Ablieferung',
    storage_in: 'Einlagerung',
    private_use: 'Privatnutzung',
    withdrawal: 'Entnahme',
    storage_out: 'Auslagerung',
    loss: 'Verlust',
};

const formatCurrency = (cents) => {
    const euros = cents / 100;
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(euros);
};

export default {
    name: 'EntityDetailsCashJournalTransactions',
    components: {
        BaseDashboardTable,
        Badge,
    },
    props: {
        transactions: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        currentMonth: {
            type: Date,
            default: () => new Date(),
        },
    },
    computed: {
        columns() {
            return [
                { key: 'timestamp', label: 'Datum', priority: 1, sortable: true },
                { key: 'type', label: 'Typ', priority: 1 },
                { key: 'description', label: 'Beschreibung', priority: 1 },
                { key: 'amount', label: 'Betrag', priority: 1, align: 'right', sortable: true },
            ];
        },
        filteredTransactions() {
            if (!this.currentMonth || !this.transactions.length) return [];

            const monthStart = startOfMonth(this.currentMonth);
            const monthEnd = endOfMonth(this.currentMonth);

            return this.transactions.filter((transaction) => {
                if (!transaction.timestamp) return false;

                const transactionDate = parseISO(transaction.timestamp);
                return isWithinInterval(transactionDate, {
                    start: monthStart,
                    end: monthEnd,
                });
            });
        },
        formattedTransactions() {
            return this.filteredTransactions.map((transaction) => ({
                ...transaction,
                typeLabel: TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type,
                formattedAmount: formatCurrency(transaction.amount || 0),
                formattedDate: transaction.timestamp
                    ? format(parseISO(transaction.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })
                    : '-',
            }));
        },
    },
    methods: {
        getBadgeColorId(type) {
            if (['income', 'deposit', 'storage_in'].includes(type)) {
                return 'color-3';
            }

            if (['private_use', 'withdrawal', 'storage_out', 'loss'].includes(type)) {
                return 'color-2';
            }
            return 'color-1';
        },
    },
};
</script>

<style lang="scss" scoped>
.EntityDetailsCashJournalTransactions {
    margin-bottom: 24px;

    .transactions-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--color-text-black, #1a1a1a);
        margin: 0 0 16px 0;
    }

    .amount-positive {
        color: #10b981;
        font-weight: 600;
    }

    .amount-negative {
        color: #ef4444;
        font-weight: 600;
    }
}
</style>
